import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { ArrowRight } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const OnboardingWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    company_name: '',
    sector: '',
    location: '',
    company_size: '',
    product_mix: '',
    batch_sizes: '',
    order_variability: '',
    user_role: '',
    pain_points: [],
    maturity_level: '',
    language_preference: 'English'
  });

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePainPointsChange = (painPoint, checked) => {
    setFormData(prev => ({
      ...prev,
      pain_points: checked 
        ? [...prev.pain_points, painPoint]
        : prev.pain_points.filter(p => p !== painPoint)
    }));
  };

  const handleNext = () => {
    if (currentStep < 4) {
      setCurrentStep(currentStep + 1);
    } else {
      submitProfile();
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const submitProfile = async () => {
    try {
      const response = await axios.post(`${API}/users`, formData);
      localStorage.setItem('userId', response.data.id);
      navigate('/assessment');
    } catch (error) {
      console.error('Failed to create user profile:', error);
    }
  };

  const painPointOptions = [
    'Long lead times',
    'High WIP levels',
    'Bottlenecks',
    'Planning chaos',
    'Team resistance to change',
    'Low digital readiness',
    'Quality issues',
    'Customer complaints',
    'Inventory management',
    'Resource allocation'
  ];

  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Company Information</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="company_name">Company Name</Label>
                <Input
                  id="company_name"
                  value={formData.company_name}
                  onChange={(e) => handleInputChange('company_name', e.target.value)}
                  placeholder="Enter your company name"
                />
              </div>
              <div>
                <Label htmlFor="sector">Industry Sector</Label>
                <Select onValueChange={(value) => handleInputChange('sector', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select your sector" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="automotive">Automotive</SelectItem>
                    <SelectItem value="aerospace">Aerospace</SelectItem>
                    <SelectItem value="electronics">Electronics</SelectItem>
                    <SelectItem value="machinery">Machinery</SelectItem>
                    <SelectItem value="metal-fabrication">Metal Fabrication</SelectItem>
                    <SelectItem value="food-beverage">Food & Beverage</SelectItem>
                    <SelectItem value="pharmaceuticals">Pharmaceuticals</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="location">Location</Label>
                <Input
                  id="location"
                  value={formData.location}
                  onChange={(e) => handleInputChange('location', e.target.value)}
                  placeholder="City, Country"
                />
              </div>
              <div>
                <Label htmlFor="company_size">Company Size (FTEs)</Label>
                <Select onValueChange={(value) => handleInputChange('company_size', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select company size" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-10">1-10 employees</SelectItem>
                    <SelectItem value="11-50">11-50 employees</SelectItem>
                    <SelectItem value="51-200">51-200 employees</SelectItem>
                    <SelectItem value="201-500">201-500 employees</SelectItem>
                    <SelectItem value="500+">500+ employees</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Manufacturing Profile</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="product_mix">Product Mix</Label>
                <Select onValueChange={(value) => handleInputChange('product_mix', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select product mix" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="high-mix-low-volume">High-mix, Low-volume</SelectItem>
                    <SelectItem value="medium-mix-medium-volume">Medium-mix, Medium-volume</SelectItem>
                    <SelectItem value="low-mix-high-volume">Low-mix, High-volume</SelectItem>
                    <SelectItem value="custom-one-off">Custom/One-off</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="batch_sizes">Typical Batch Sizes</Label>
                <Select onValueChange={(value) => handleInputChange('batch_sizes', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select batch sizes" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1-5">1-5 pieces</SelectItem>
                    <SelectItem value="6-20">6-20 pieces</SelectItem>
                    <SelectItem value="21-100">21-100 pieces</SelectItem>
                    <SelectItem value="100+">100+ pieces</SelectItem>
                    <SelectItem value="mixed">Mixed batch sizes</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="order_variability">Order Variability</Label>
                <Select onValueChange={(value) => handleInputChange('order_variability', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select variability" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="very-high">Very High (daily changes)</SelectItem>
                    <SelectItem value="high">High (weekly changes)</SelectItem>
                    <SelectItem value="medium">Medium (monthly changes)</SelectItem>
                    <SelectItem value="low">Low (quarterly changes)</SelectItem>
                    <SelectItem value="very-low">Very Low (stable orders)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="user_role">Your Role</Label>
                <Select onValueChange={(value) => handleInputChange('user_role', value)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select your role" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="plant-manager">Plant Manager</SelectItem>
                    <SelectItem value="production-manager">Production Manager</SelectItem>
                    <SelectItem value="operations-manager">Operations Manager</SelectItem>
                    <SelectItem value="supervisor">Supervisor</SelectItem>
                    <SelectItem value="team-leader">Team Leader</SelectItem>
                    <SelectItem value="continuous-improvement">Continuous Improvement</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Current Challenges</h2>
            <p className="text-gray-600">Select all pain points that apply to your current situation:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {painPointOptions.map((painPoint) => (
                <div key={painPoint} className="flex items-center space-x-3">
                  <Checkbox
                    id={painPoint}
                    checked={formData.pain_points.includes(painPoint)}
                    onCheckedChange={(checked) => handlePainPointsChange(painPoint, checked)}
                  />
                  <Label htmlFor={painPoint} className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">
                    {painPoint}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-gray-900">Experience Level</h2>
            <div>
              <Label htmlFor="maturity_level">Your QRM/Lean/TOC Experience</Label>
              <Select onValueChange={(value) => handleInputChange('maturity_level', value)}>
                <SelectTrigger>
                  <SelectValue placeholder="Select your experience level" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="novice">Novice (New to these methodologies)</SelectItem>
                  <SelectItem value="some-experience">Some Experience (1-2 years)</SelectItem>
                  <SelectItem value="experienced">Experienced (3-5 years)</SelectItem>
                  <SelectItem value="expert">Expert (5+ years)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="language_preference">Preferred Language</Label>
              <Select 
                value={formData.language_preference}
                onValueChange={(value) => handleInputChange('language_preference', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="English">English</SelectItem>
                  <SelectItem value="Dutch">Dutch</SelectItem>
                  <SelectItem value="French">French</SelectItem>
                  <SelectItem value="German">German</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 py-12">
      <div className="container mx-auto px-6">
        <div className="max-w-3xl mx-auto">
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Welcome to Your QRM Journey</h1>
            <p className="text-xl text-gray-600">Let's gather some information to personalize your experience</p>
          </div>

          {/* Progress Indicator */}
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm text-gray-600">Step {currentStep} of 4</span>
              <span className="text-sm text-gray-600">{Math.round((currentStep / 4) * 100)}% Complete</span>
            </div>
            <Progress value={(currentStep / 4) * 100} className="h-2" />
          </div>

          <Card className="p-8 bg-white shadow-2xl">
            {renderStep()}

            <div className="flex justify-between mt-8">
              <Button
                variant="outline"
                onClick={handleBack}
                disabled={currentStep === 1}
                className="px-8"
              >
                Back
              </Button>
              <Button
                onClick={handleNext}
                className="px-8 bg-blue-600 hover:bg-blue-700"
              >
                {currentStep === 4 ? 'Complete Setup' : 'Next'}
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default OnboardingWizard;
